<template>
  <section class="fpb" :class="'fpb--' + kind">
    <div v-if="loading" class="fpb-skeleton" aria-hidden="true">
      <div class="fpb-skeleton-bar fpb-skeleton-bar--lg"></div>
      <div class="fpb-skeleton-bar"></div>
      <div class="fpb-skeleton-bar fpb-skeleton-bar--md"></div>
      <div class="fpb-skeleton-label">{{ $t('page_2.file_preview.loading_preview') }}</div>
    </div>

    <div v-else-if="error" class="fpb-state">
      <svg class="fpb-state-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="9"/><path d="M12 7v5"/><circle cx="12" cy="16" r="0.6" fill="currentColor"/>
      </svg>
      <p class="fpb-state-title">{{ $t('page_2.file_preview.preview_failed') }}</p>
      <p class="fpb-state-sub">{{ error }}</p>
      <div class="fpb-state-actions">
        <button type="button" class="fpb-btn fpb-btn--ghost" @click="dispatch">
          {{ $t('page_2.file_preview.retry') }}
        </button>
      </div>
    </div>

    <template v-else>
      <iframe
        v-if="kind === 'pdf' && pdfBlobUrl"
        :src="pdfBlobUrl"
        class="fpb-frame"
        :title="fileItem.name"
      ></iframe>

      <iframe
        v-else-if="kind === 'office' && previewGetUrl"
        :src="previewGetUrl"
        class="fpb-frame"
        :title="fileItem.name"
      ></iframe>

      <div v-else-if="kind === 'image'" class="fpb-img-wrap">
        <img
          v-if="imageThumbUrl && !imageFullReady"
          :src="imageThumbUrl"
          :alt="fileItem.name"
          class="fpb-img fpb-img--thumb"
        />
        <img
          v-if="downloadUrl"
          :src="downloadUrl"
          :alt="fileItem.name"
          class="fpb-img"
          :class="{ 'fpb-img--ready': imageFullReady }"
          @load="imageFullReady = true"
        />
      </div>

      <video
        v-else-if="kind === 'video' && downloadUrl"
        :src="downloadUrl"
        :poster="imageThumbUrl || ''"
        class="fpb-media"
        controls
        preload="metadata"
      ></video>

      <audio
        v-else-if="kind === 'audio' && downloadUrl"
        :src="downloadUrl"
        class="fpb-audio"
        controls
        preload="metadata"
      ></audio>

      <pre v-else-if="kind === 'text'" class="fpb-text">{{ textContent }}</pre>

      <div v-else class="fpb-state">
        <svg class="fpb-state-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/>
        </svg>
        <p class="fpb-state-title">{{ $t('page_2.file_preview.unsupported_title') }}</p>
        <p class="fpb-state-sub">{{ $t('page_2.file_preview.unsupported_subtitle') }}</p>
      </div>
    </template>
  </section>
</template>

<script>
import sharepoint from "@/apiCallers/promise_handler_db/sharepoint";
import { classifyFile } from "@/helpers/file";

export default {
  name: "FilePreviewBody",
  props: {
    fileItem: { type: Object, default: null },
    groupId: { type: String, default: "" },
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      error: null,
      previewGetUrl: null,
      pdfBlobUrl: null,
      textContent: "",
      imageFullReady: false,
      lastLoadedId: null,
    };
  },
  computed: {
    kind() {
      return classifyFile(this.fileItem);
    },
    downloadUrl() {
      return this.fileItem && this.fileItem["@microsoft.graph.downloadUrl"];
    },
    imageThumbUrl() {
      const t = this.fileItem && this.fileItem.thumbnails;
      if (!t || !t.length) return null;
      const first = t[0];
      return (first.large && first.large.url) ||
             (first.medium && first.medium.url) ||
             (first.small && first.small.url) || null;
    },
  },
  watch: {
    active(open) {
      if (open) this.dispatch();
      else this.reset();
    },
    fileItem(next, prev) {
      if (!this.active) return;
      const nextId = next && next.id;
      const prevId = prev && prev.id;
      if (nextId !== prevId) this.dispatch();
    },
  },
  mounted() {
    if (this.active && this.fileItem) this.dispatch();
  },
  beforeDestroy() {
    this.revokePdfBlob();
  },
  methods: {
    reset() {
      this.loading = false;
      this.error = null;
      this.previewGetUrl = null;
      this.revokePdfBlob();
      this.textContent = "";
      this.imageFullReady = false;
      this.lastLoadedId = null;
    },
    revokePdfBlob() {
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl);
        this.pdfBlobUrl = null;
      }
    },
    async dispatch() {
      this.error = null;
      this.previewGetUrl = null;
      this.revokePdfBlob();
      this.textContent = "";
      this.imageFullReady = false;

      if (!this.fileItem) return;
      this.lastLoadedId = this.fileItem.id;

      if (this.kind === "pdf") {
        await this.fetchPdfBlob();
      } else if (this.kind === "office") {
        await this.fetchOfficePreview();
      } else if (this.kind === "text") {
        await this.fetchTextContent();
      }
      if (!this.error) this.$emit("loaded");
    },
    async fetchPdfBlob() {
      if (!this.downloadUrl) return;
      this.loading = true;
      try {
        const res = await fetch(this.downloadUrl);
        if (!res.ok) throw new Error("HTTP " + res.status);
        const blob = await res.blob();
        this.pdfBlobUrl = URL.createObjectURL(
          blob.type ? blob : new Blob([blob], { type: "application/pdf" })
        );
      } catch (e) {
        console.error("[FilePreviewBody] PDF fetch failed:", e);
        this.error = this.$t("page_2.file_preview.preview_failed");
        this.$emit("error", e);
      } finally {
        this.loading = false;
      }
    },
    async fetchOfficePreview() {
      if (!this.groupId || !this.fileItem.id) {
        this.error = this.$t("page_2.file_preview.preview_failed");
        return;
      }
      this.loading = true;
      try {
        const res = await sharepoint.getPreviewUrl(this.groupId, this.fileItem.id);
        const url = res && res.data && res.data.getUrl;
        if (!url) throw new Error("Empty preview URL");
        this.previewGetUrl = url;
      } catch (e) {
        console.error("[FilePreviewBody] office preview failed:", e);
        this.error = this.$t("page_2.file_preview.preview_failed");
        this.$emit("error", e);
      } finally {
        this.loading = false;
      }
    },
    async fetchTextContent() {
      if (!this.downloadUrl) return;
      this.loading = true;
      try {
        const res = await fetch(this.downloadUrl);
        if (!res.ok) throw new Error("HTTP " + res.status);
        this.textContent = await res.text();
      } catch (e) {
        console.error("[FilePreviewBody] text fetch failed:", e);
        this.error = this.$t("page_2.file_preview.preview_failed");
        this.$emit("error", e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.fpb {
  position: relative;
  background: #f3f7fc;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  overflow: hidden;
  contain: layout paint;
  font-family: "Barlow", sans-serif;
}

.fpb--pdf,
.fpb--office {
  padding: 0;
}
.fpb--image,
.fpb--video,
.fpb--audio {
  padding: 1.25rem;
  align-items: center;
  justify-content: center;
}
.fpb--text,
.fpb--unsupported {
  padding: 1.5rem;
  overflow: auto;
  align-items: stretch;
  justify-content: center;
}

.fpb-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: #f3f7fc;
  display: block;
  transform: translateZ(0);
  will-change: transform;
}

.fpb-img-wrap {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.fpb-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  border-radius: 6px;
  box-shadow: 0 4px 20px -6px rgba(43, 43, 65, 0.2);
  background: #fff;
  opacity: 1;
  transition: opacity 240ms ease;
}
.fpb-img--thumb {
  position: absolute;
  inset: 1.25rem;
  margin: auto;
  filter: blur(6px);
  transform: scale(1.02);
}
.fpb-img:not(.fpb-img--thumb) {
  opacity: 0;
}
.fpb-img--ready {
  opacity: 1 !important;
}

.fpb-media {
  max-width: 100%;
  max-height: 100%;
  background: #000;
  outline: none;
  border-radius: 6px;
  box-shadow: 0 4px 20px -6px rgba(43, 43, 65, 0.25);
}

.fpb-audio {
  width: min(560px, 92%);
}

.fpb-text {
  margin: 0;
  width: 100%;
  min-height: 100%;
  padding: 1.5rem 1.75rem;
  font-family: "JetBrains Mono", ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #2b2b41;
  background: #fff;
  border-radius: 0.6rem;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  box-sizing: border-box;
  box-shadow: 0 1px 4px rgba(43, 43, 65, 0.06);
}

.fpb-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 2rem;
  width: 100%;
  max-width: 640px;
  margin: auto;
}
.fpb-skeleton-bar {
  height: 1.1rem;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(43, 43, 65, 0.06) 0%,
    rgba(43, 43, 65, 0.12) 50%,
    rgba(43, 43, 65, 0.06) 100%
  );
  background-size: 200% 100%;
  animation: fpb-shimmer 1.4s ease-in-out infinite;
}
.fpb-skeleton-bar--lg { height: 2rem; width: 60%; }
.fpb-skeleton-bar--md { width: 80%; }
.fpb-skeleton-label {
  margin-top: 1rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #6b6b7d;
  text-align: center;
  letter-spacing: 0.04em;
}
@keyframes fpb-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.fpb-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2.5rem 1.5rem;
  max-width: 440px;
  margin: auto;
}
.fpb-state-icon {
  color: #b8b8c1;
  margin-bottom: 1rem;
}
.fpb-state-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2b2b41;
  margin: 0 0 0.4rem 0;
}
.fpb-state-sub {
  font-size: 0.95rem;
  font-weight: 500;
  color: #6b6b7d;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}
.fpb-state-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.fpb-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.2rem;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: background 160ms ease, color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}
.fpb-btn--ghost {
  background: #eceef3;
  color: #2b2b41;
}
.fpb-btn--ghost:hover {
  background: #fff;
  box-shadow: 0 2px 8px rgba(43, 43, 65, 0.12);
  color: #3325AE;
}

.fpb ::-webkit-scrollbar,
.fpb-text::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
.fpb ::-webkit-scrollbar-track,
.fpb-text::-webkit-scrollbar-track {
  background: transparent;
}
.fpb ::-webkit-scrollbar-thumb,
.fpb-text::-webkit-scrollbar-thumb {
  background: rgba(43, 43, 65, 0.22);
  border-radius: 10px;
  border: 2px solid transparent;
  background-clip: padding-box;
}
.fpb ::-webkit-scrollbar-thumb:hover,
.fpb-text::-webkit-scrollbar-thumb:hover {
  background: rgba(43, 43, 65, 0.36);
}
</style>
